import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { CategoryPills } from "../components/CategoryPills";
import { GameGrid } from "../components/GameGrid";
import { placeholderGames } from "../data/placeholderGames";
import {
	ARCHETYPE_LABELS,
	ARCHETYPES,
	type Archetype,
	type SortMode,
} from "../types/Game";

const SORT_OPTIONS: { value: SortMode; label: string }[] = [
	{ value: "trending", label: "Trending" },
	{ value: "popular", label: "Most Played" },
	{ value: "new", label: "New" },
	{ value: "top-rated", label: "Top Rated" },
];

/**
 * Browse page. Category pill filter + sort dropdown + full grid of every game.
 * Category state is mirrored to the `?category=` query param so category
 * links from the home page deep-link correctly, and so the browser back
 * button restores your filter.
 */
export function BrowseRoute() {
	const [params, setParams] = useSearchParams();
	const categoryParam = params.get("category");

	const initialCategory: Archetype | null =
		categoryParam && (ARCHETYPES as readonly string[]).includes(categoryParam)
			? (categoryParam as Archetype)
			: null;

	const [selected, setSelected] = useState<Archetype | null>(initialCategory);
	const [sort, setSort] = useState<SortMode>("trending");

	useEffect(() => {
		document.title = selected
			? `${ARCHETYPE_LABELS[selected]} Games · GenieWorlds`
			: "Browse all games · GenieWorlds";
	}, [selected]);

	// Keep URL in sync when the user clicks a pill
	useEffect(() => {
		const next = new URLSearchParams(params);
		if (selected) {
			next.set("category", selected);
		} else {
			next.delete("category");
		}
		// Only update if changed, avoid infinite loops
		if (next.toString() !== params.toString()) {
			setParams(next, { replace: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selected]);

	// Keep local state in sync if the query param changes from outside
	useEffect(() => {
		if (initialCategory !== selected) {
			setSelected(initialCategory);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [categoryParam]);

	const selectedLabel = selected ? ARCHETYPE_LABELS[selected] : null;

	const filtered = useMemo(() => {
		const base = selected
			? placeholderGames.filter((g) => g.archetype === selected)
			: placeholderGames;
		const sorted = [...base];
		switch (sort) {
			case "popular":
				sorted.sort((a, b) => b.plays - a.plays);
				break;
			case "new":
				sorted.sort((a, b) =>
					a.publishedAt < b.publishedAt ? 1 : -1,
				);
				break;
			case "top-rated":
				sorted.sort((a, b) => b.rating - a.rating);
				break;
			case "trending":
			default:
				sorted.sort((a, b) => {
					const at = a.trending ? 1 : 0;
					const bt = b.trending ? 1 : 0;
					if (at !== bt) return bt - at;
					return b.plays - a.plays;
				});
				break;
		}
		return sorted;
	}, [selected, sort]);

	return (
		<div className="mx-auto max-w-7xl px-4 md:px-6 py-6 md:py-8 space-y-6">
			<header className="space-y-2">
				<h1 className="font-display font-extrabold text-3xl md:text-4xl text-ink">
					Browse all games
				</h1>
				<p className="text-sm text-ink-muted">
					{filtered.length}{" "}
					{filtered.length === 1 ? "game" : "games"}
					{selectedLabel ? ` in ${selectedLabel}` : ""} — pick a
					genre or sort to find your next favorite
				</p>
			</header>

			<CategoryPills selected={selected} onChange={setSelected} />

			<div className="flex items-center justify-between gap-3">
				<div className="text-xs text-ink-faint uppercase tracking-wider">
					Sort by
				</div>
				<div className="flex gap-1 rounded-full bg-surface border border-border p-1">
					{SORT_OPTIONS.map((opt) => (
						<button
							key={opt.value}
							type="button"
							onClick={() => setSort(opt.value)}
							className={`px-3 h-8 rounded-full text-xs font-semibold transition-colors ${
								sort === opt.value
									? "bg-primary text-background"
									: "text-ink-muted hover:text-ink"
							}`}
						>
							{opt.label}
						</button>
					))}
				</div>
			</div>

			<GameGrid games={filtered} />
		</div>
	);
}
